import {useState, useEffect, useRef} from "react";
import {useQuery} from "@tanstack/react-query";
import {trpc} from "@/app/trpc";
import {Loader, StickyHeader, YStack} from "@chayka/ui";
import {useNavigate} from "react-router-dom";
import {NewsFeedItem} from "./ui/NewsFeedItem";

const PAGE_SIZE = 10;

export const NewsPage = () => {
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  const {data, isFetching} = useQuery(trpc.news.list.queryOptions({offset, count: PAGE_SIZE}));

  const [allNews, setAllNews] = useState<NonNullable<typeof data>>([]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(isFetching);
  const hasMoreRef = useRef(hasMore);

  isFetchingRef.current = isFetching;
  hasMoreRef.current = hasMore;

  useEffect(() => {
    if (!data) return;
    setAllNews((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      return [...prev, ...data.filter((n) => !existingIds.has(n.id))];
    });
    if (data.length < PAGE_SIZE) setHasMore(false);
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isFetchingRef.current && hasMoreRef.current) {
        setOffset((prev) => prev + PAGE_SIZE);
      }
    });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <YStack>
      <StickyHeader title="Новости" />
      <YStack>
        {allNews.map((item) => (
          <NewsFeedItem
            key={item.id}
            image={item.mediaId}
            date={item.date}
            title={item.title}
            excerpt={item.excerpt}
            onClick={() => navigate(String(item.id), {relative: "path", state: {news: item}})}
          />
        ))}
      </YStack>
      <div ref={sentinelRef} />
      {isFetching && (
        <div className="w-full h-full flex justify-center items-center p-5">
          <Loader />
        </div>
      )}
    </YStack>
  );
};
